import { useEffect, useId, useRef, useState } from "react";
import { dayChoices, dayOf, hasDay, timeOf } from "../../src/plan-day.ts";
import { ApiError, api, type Item, type TripView } from "./api.ts";
import { tripZone } from "./bookingDraft.ts";
import { dayLabel, localToday, zoneCity } from "./format.ts";
import { asLabel } from "./former.tsx";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button, Icon } from "./souvenir.ts";
import { type Lookup, loadLookup, zoneAt } from "./tzLookup.ts";
import { deviceZone } from "./zone.ts";

// Plan for a day (DESIGN.md, Views, the web's own edits; features/days.md, "Plan for a day"): a place or goal put on
// one of the trip's days, before the trip as during it, from its card or from Days' Wanted, not placed; a time if I
// like, in its own time zone (the item's, else its place's, else the trip's); on something with a day, Move (the
// same form) and Take off its day. Each one write, signed "on the web", undoable at once (the trip's notice) and
// from Updates, over the item as it was read: someone's change meanwhile is never overwritten.

const isDate = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);

// What the trip's notice says of it: "Planned Nishiki Market for Sat 5 Dec · 10:00".
function doneText(item: Item, day: string | null, time: string, moving: boolean): string {
  if (day === null) return `Took ${item.title} off its day`;
  const when = `${dayLabel(day)}${time ? ` · ${time}` : ""}`;
  return moving ? `Moved ${item.title} to ${when}` : `Planned ${item.title} for ${when}`;
}

// The form: the trip's days to pick from, a time if I like (on a date), Save and Cancel. `onClose`: closed, saved or
// not (saved, once the trip has loaded with it, so whatever it's in never shows it as it was).
export function PlanDayForm({
  item,
  trip,
  items,
  onChanged,
  onClose,
}: {
  item: Item;
  trip: TripView;
  // Everything in the trip: the days things are on (a trip without dates), and the trip's usual time zone.
  items: Item[];
  onChanged: () => void;
  onClose: () => void;
}) {
  const id = useId();
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const moving = hasDay(item.when);
  // The item as the form read it: its version is what the change is written over.
  const [base, setBase] = useState(item);
  const choices = dayChoices(
    trip.dates,
    items.flatMap((i) => dayOf(i.when) ?? []),
    dayOf(base.when),
  );
  // Its day now, as the list has it (a "day N" is its date once the trip has dates); with none, today while it's
  // one of the trip's days, else nothing chosen yet.
  const now = dayOf(base.when);
  const nowChoice = now
    ? (choices.find((c) => c.value === now) ?? choices.find((c) => c.n > 0 && now === `day ${c.n}`))
    : null;
  const today = localToday();
  const [day, setDay] = useState(() => nowChoice?.value ?? (choices.some((c) => c.value === today) ? today : ""));
  const [time, setTime] = useState(() => timeOf(base.when) ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [meanwhile, setMeanwhile] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Saved, at this version: closed once the trip has loaded with it (or after a while all the same).
  const [saved, setSaved] = useState<number | null>(null);
  const [lookup, setLookup] = useState<Lookup | null>(null);
  useEffect(() => {
    let live = true;
    loadLookup().then((l) => live && l && setLookup(() => l));
    return () => {
      live = false;
    };
  }, []);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (saved === null) return;
    if (item.version >= saved) return close.current();
    const timer = setTimeout(() => close.current(), 5000);
    return () => clearTimeout(timer);
  }, [saved, item.version]);
  // Opened: the day's list takes the focus; closed, the focus goes back to what opened it (the caller's).
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    form.current?.querySelector<HTMLElement>("select")?.focus({ preventScroll: true });
    form.current?.scrollIntoView({ block: "nearest" });
  }, []);

  // Its time zone: the item's own, else its place's, else the trip's usual one.
  const zone =
    base.when && (base.when.precision === "exact" || base.when.precision === "window")
      ? base.when.tz
      : (zoneAt(base, lookup) ?? tripZone(items, deviceZone()));
  const dated = isDate(day) || (day === "" && choices.some((c) => isDate(c.value)));

  const save = async () => {
    if (offline || busy || saved !== null) return;
    if (!day) {
      setProblem("Choose a day.");
      form.current?.querySelector<HTMLElement>("select")?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    const at = time && isDate(day) ? { time, tz: zone } : {};
    try {
      const { change, version } = await api.planDay(trip.id, item.id, { version: base.version, day, ...at });
      onChanged();
      if (change) say({ change, text: doneText(item, day, "time" in at ? time : "", moving) });
      if (change) setSaved(version);
      else onClose();
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.status === 409 && e.data["item"]) {
        const fresh = e.data["item"] as Item;
        const by = fresh.updated?.by;
        setBase(fresh);
        setMeanwhile(by?.id === trip.you ? "You" : asLabel(by?.name ?? "Someone"));
        onChanged();
        return;
      }
      setError(e instanceof Error ? e.message : "Couldn't save it. Try again.");
    }
  };

  const title = moving ? "Move to another day" : "Plan for a day";
  return (
    <form
      ref={form}
      className="tm-edit-form tm-plan-day"
      aria-labelledby={`${id}-title`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        // Leaving the form, before the page's own Escape (which closes the card).
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <h2 id={`${id}-title`} className="title">
        {title}
      </h2>
      {meanwhile && (
        <p className="callout tm-booking-note is-meanwhile" role="alert">
          <Icon name="people" size={20} className="tm-booking-icon" />
          <span>
            {meanwhile === "You" ? "You changed it elsewhere" : `${meanwhile} changed it`} while this was open, so
            nothing was saved. Check the day and save again.
          </span>
        </p>
      )}
      <div className="tm-plan-day-fields">
        <div className={`sv-field tm-booking-field${problem ? " is-wrong" : ""}`}>
          <label className="sv-field-label" htmlFor={`${id}-day`}>
            Day
          </label>
          <span className="tm-booking-select">
            <select
              id={`${id}-day`}
              className="sv-field-input"
              value={day}
              aria-invalid={problem ? true : undefined}
              onChange={(e) => {
                setDay(e.target.value);
                setProblem(null);
              }}
            >
              {!day && <option value="">Choose a day</option>}
              {choices.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <span className="tm-booking-chevron" aria-hidden="true">
              <Icon name="chevron" size={16} />
            </span>
          </span>
          {problem && <span className="sv-field-hint tm-error">{problem}</span>}
        </div>
        {dated && (
          <div className="sv-field tm-booking-field tm-plan-day-time">
            <label className="sv-field-label" htmlFor={`${id}-time`}>
              Time
            </label>
            <input
              id={`${id}-time`}
              className={`sv-field-input${time ? "" : " is-empty"}`}
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
        )}
      </div>
      <p className="caption tm-muted">
        {dated
          ? `A time if you like, where it is (${zoneCity(zone)} time); leave it out for the day alone.`
          : "The trip has no dates yet, so its days count from the first. A time goes on once it has dates."}
      </p>
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal.</span>
        </p>
      )}
      {error && (
        <p className="caption tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <span className="tm-note-actions">
        <Button variant="primary" size="sm" type="submit" disabled={busy || saved !== null || offline}>
          {busy || saved !== null ? "Saving…" : "Save"}
        </Button>
        <Button variant="quiet" size="sm" type="button" onClick={onClose}>
          Cancel
        </Button>
      </span>
    </form>
  );
}

// Take off its day: one tap, Undo at once in the trip's notice. A part of the day it had stays with it.
export function TakeOffDay({
  item,
  trip,
  onChanged,
  className,
}: {
  item: Item;
  trip: TripView;
  onChanged: () => void;
  className?: string;
}) {
  const say = useJustDone();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const take = async () => {
    setBusy(true);
    setError(null);
    try {
      const { change } = await api.planDay(trip.id, item.id, { version: item.version, day: null });
      onChanged();
      if (change) say({ change, text: doneText(item, null, "", true) });
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 409
          ? "Someone changed it meanwhile, so nothing changed. Look again and try again."
          : e instanceof Error
            ? e.message
            : "Couldn't take it off its day. Try again.",
      );
      if (e instanceof ApiError && e.status === 409) onChanged();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button variant="quiet" size="sm" icon="close" className={className} disabled={busy || !online} onClick={take}>
        {busy ? "Taking it off…" : "Take off its day"}
      </Button>
      {error && (
        <p className="caption tm-error tm-card-actions-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </>
  );
}
